import { useRef } from "react";
import { formatDate } from "../../api/dates";
import { formatPercent } from "../../api/money";
import { useAttach, type QuotationDetail } from "../../api/quotations";
import { KitPanel } from "./KitPanel";
import { Decision, DeleteQuotation, History, Mileage } from "./QuoteDecision";
import { QuoteFoot, QuoteHeaderForm, QuoteLines, StandardLines } from "./QuoteLines";

/** The Supplier quote step: the quotation the job hangs from. What it is and
 * where it came from, the lines, the decision beside the totals, and below
 * them the record -- mileage for the office, and the history. It merges what
 * were two tabs, "Order" and "Supplier's quote" (Fix #11: "Order" was easily
 * read as the purchase order). */
export default function QuoteStep({ detail }: { detail: QuotationDetail }) {
  const editable = detail.actions.can_edit;
  return (
    <section className="cm-docview" aria-label={`Quotation ${detail.number}`}>
      <dl className="cm-facts">
        <div>
          <dt>Supplier</dt>
          <dd>
            {detail.supplier
              ? `${detail.supplier.name} (${detail.supplier.acc_no}) · ${formatPercent(detail.supplier.discount_pct ?? "0")} standing discount`
              : "—"}
          </dd>
        </div>
        <div>
          <dt>Works order</dt>
          <dd>{detail.inspection?.work_order_no ?? "—"}</dd>
        </div>
        <div>
          <dt>Dated</dt>
          <dd>{formatDate(detail.quote_date)}</dd>
        </div>
        <div>
          <dt>Valid until</dt>
          <dd>
            {detail.valid_until ? formatDate(detail.valid_until) : "Not stated"}
            {detail.expired ? " (lapsed)" : ""}
          </dd>
        </div>
        <div>
          <dt>Version</dt>
          <dd>{detail.version}</dd>
        </div>
      </dl>

      <Documents detail={detail} />

      {detail.uncovered.length > 0 ? (
        <div className="notice warn" role="note">
          Section 9 lists {detail.uncovered.length === 1 ? "a part" : "parts"} no line covers:{" "}
          {detail.uncovered.map((part, index) => (
            <span key={part.id}>
              {index > 0 ? "; " : ""}
              <strong>
                {part.code ?? "—"} {part.description}
                {part.size ? ` ${part.size}` : ""}
              </strong>
            </span>
          ))}
          . Add a line for {detail.uncovered.length === 1 ? "it" : "each"} or note why it isn't needed.
        </div>
      ) : null}

      {editable ? <QuoteHeaderForm detail={detail} /> : null}
      <QuoteLines detail={detail} />
      <KitPanel detail={detail} />
      <StandardLines detail={detail} />

      <div className="cm-doc-bottom">
        <div className="cm-doc-bottom-text">
          <Decision detail={detail} />
        </div>
        <QuoteFoot detail={detail} />
      </div>

      <Mileage detail={detail} />
      <History detail={detail} />
      <DeleteQuotation detail={detail} />
    </section>
  );
}

/** The quotation on the supplier's own template, as quoted, and the PDF they
 * sent, with the way to attach or replace it. The full-price copy that goes
 * with the BOQ is offered on the Customer BOQ step, where it is used. */
function Documents({ detail }: { detail: QuotationDetail }) {
  const attach = useAttach(detail.id);
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="cm-doc-links">
      <a className="btn" href={detail.pdf_url} target="_blank" rel="noreferrer">
        Quotation (PDF)
      </a>
      {detail.has_attachment ? (
        <a className="btn" href={`/api/v1/quotations/${detail.id}/attachment`} target="_blank" rel="noreferrer">
          Supplier's own PDF
        </a>
      ) : null}
      {detail.actions.can_edit ? (
        <>
          <input
            ref={input}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                attach.mutate(file);
              }
              event.target.value = "";
            }}
          />
          <button type="button" className="btn" onClick={() => input.current?.click()} disabled={attach.isPending}>
            {detail.has_attachment ? "Replace their PDF" : "Attach their PDF"}
          </button>
        </>
      ) : null}
      {detail.letterhead_gaps.length > 0 ? (
        <span className="cm-doc-meta warn">Letterhead missing: {detail.letterhead_gaps.join(", ")}</span>
      ) : null}
      {attach.isError ? <p className="notice bad">{attach.error.message}</p> : null}
    </div>
  );
}
